/**
 * Test Data Generator
 * 
 * Shared test data generator for condition analyzer.
 * Used by both frontend and scripts.
 */

import { 
    AnalyzerDataPoint, 
    Condition, 
    TestScenario,
    TestScenarioFirestore,
    AnalyzerDataPointFirestore
} from './condition-analyzer.models';

/**
 * Generate a date sequence starting from a base date
 */
function generateDates(count: number, intervalDays = 1, startDate?: Date): Date[] {
    const start = startDate || new Date();
    const dates: Date[] = [];
    
    for (let i = 0; i < count; i++) {
        const date = new Date(start);
        date.setDate(start.getDate() + (i * intervalDays));
        dates.push(date);
    }
    
    return dates;
}

/**
 * Add random noise to a value
 */
function addNoise(value: number, noisePercent = 0.05): number {
    const noise = value * noisePercent * (Math.random() * 2 - 1);
    return value + noise;
}

/**
 * Create data points from values
 */
function createDataPoints(values: number[], dates: Date[]): AnalyzerDataPoint[] {
    let compounded = 0;
    return values.map((value, index) => {
        compounded += value;
        return {
            date: dates[index],
            value: value,
            valueCompounded: compounded,
            index
        };
    });
}

/**
 * Generate NON-EXISTENCE scenario
 * Activity with ups/downs → STEEP CRASH (fast, not gradual) → Flatline near zero
 * Based on user's drawing: M-like activity, then near-vertical drop, then flatline
 */
export function generateNonExistence(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    // Pattern based on user's drawing:
    // First ~50%: Activity with ups and downs (like M pattern)
    // Next ~15%: STEEP crash (near vertical - this is key!)
    // Last ~35%: Flatline near zero
    
    const activityEndIndex = Math.floor(pointCount * 0.5);  // 15 points of activity
    const crashEndIndex = Math.floor(pointCount * 0.65);    // 4-5 points of crash
    
    for (let i = 0; i < pointCount; i++) {
        if (i < activityEndIndex) {
            // M-like activity pattern: up, down, up, down
            const phase = (i / activityEndIndex) * 2 * Math.PI;
            const baseValue = 55 + Math.sin(phase * 2) * 20; // Oscillates 35-75
            values.push(addNoise(baseValue, 0.08));
        } else if (i < crashEndIndex) {
            // STEEP crash over just 4-5 points (near vertical drop)
            const crashProgress = (i - activityEndIndex) / (crashEndIndex - activityEndIndex);
            const crashValue = 55 - (crashProgress * 52); // Drop from ~55 to ~3
            values.push(Math.max(2, addNoise(crashValue, 0.05)));
        } else {
            // Flatline near zero
            values.push(addNoise(3, 0.3)); // Values around 2-4
        }
    }
    
    return {
        id: 'non-existence',
        name: 'Non-Existence',
        description: 'Activity then STEEP crash then flatline - fell off the chart',
        expectedCondition: Condition.NON_EXISTENCE,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate DANGER scenario
 * Sharp, sustained decline - but does NOT crash to zero (that's Non-Existence)
 * Danger = declining rapidly toward trouble, but still has some activity
 */
export function generateDanger(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    // Start high, drop to moderate-low (not near-zero)
    // This is a decline, not a crash - ends around 20-30 range
    const startValue = 100;
    const endValue = 25;
    const slopePerPoint = (endValue - startValue) / (pointCount - 1);
    
    for (let i = 0; i < pointCount; i++) {
        const baseValue = startValue + (slopePerPoint * i);
        values.push(addNoise(baseValue, 0.08));
    }
    
    return {
        id: 'danger',
        name: 'Danger',
        description: 'Sharp sustained decline - still has activity (not crashed)',
        expectedCondition: Condition.DANGER,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate EMERGENCY scenario
 * Drop followed by recovery
 */
export function generateEmergency(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const startValue = 80;
    const lowestPoint = Math.floor(pointCount * 0.5); // Bottom out at 50%
    const minValue = 20;
    
    for (let i = 0; i < pointCount; i++) {
        let value: number;
        
        if (i < lowestPoint) {
            // Dropping phase
            const dropProgress = i / lowestPoint;
            value = startValue - (startValue - minValue) * dropProgress;
        } else {
            // Recovery phase
            const recoveryProgress = (i - lowestPoint) / (pointCount - lowestPoint);
            // Recover to about 60-70% of original (partial recovery)
            const recoveryTarget = startValue * 0.7;
            value = minValue + (recoveryTarget - minValue) * recoveryProgress;
        }
        
        values.push(addNoise(value, 0.08));
    }
    
    return {
        id: 'emergency',
        name: 'Emergency',
        description: 'Drop followed by recovery - bottoming out then rising',
        expectedCondition: Condition.EMERGENCY,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate NORMAL OPERATION scenario
 * Stable with minor fluctuations around a mean
 */
export function generateNormal(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const baseValue = 50;
    const values = Array(pointCount).fill(0).map((_, i) => {
        // Add slight sine wave for natural fluctuation
        const wave = Math.sin(i * 0.3) * 5;
        return addNoise(baseValue + wave, 0.08);
    });
    
    return {
        id: 'normal',
        name: 'Normal Operation',
        description: 'Stable trend with minor fluctuations (±10%)',
        expectedCondition: Condition.NORMAL,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate AFFLUENCE scenario
 * Sharp, sudden rise
 */
export function generateAffluence(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const startValue = 30;
    const growthRate = 0.12; // 12% growth per period
    
    for (let i = 0; i < pointCount; i++) {
        const baseValue = startValue * Math.pow(1 + growthRate, i);
        values.push(addNoise(baseValue, 0.06));
    }
    
    return {
        id: 'affluence',
        name: 'Affluence',
        description: 'Sharp sustained rise - rapid improvement',
        expectedCondition: Condition.AFFLUENCE,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate POWER scenario
 * High values, stable plateau - must stay consistently high
 */
export function generatePower(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const highValue = 95;
    const values = Array(pointCount).fill(0).map(() => {
        // Very small fluctuation around high value - no trend, just minor noise
        return addNoise(highValue, 0.02); // Only 2% noise for stability
    });
    
    return {
        id: 'power',
        name: 'Power',
        description: 'Sustained high performance - stable at top',
        expectedCondition: Condition.POWER,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate edge case: Slow gradual decline (borderline Danger/Normal)
 */
export function generateSlowDecline(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const startValue = 60;
    const declineRate = 0.02; // 2% decline per period - slow
    
    for (let i = 0; i < pointCount; i++) {
        const baseValue = startValue * Math.pow(1 - declineRate, i);
        values.push(addNoise(baseValue, 0.05));
    }
    
    return {
        id: 'slow-decline',
        name: 'Slow Decline (Edge Case)',
        description: 'Gradual decline - borderline Danger/Normal',
        expectedCondition: Condition.DANGER, // Should still be danger, just less confidence
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate edge case: Slow gradual rise (borderline Affluence/Normal)
 */
export function generateSlowRise(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const startValue = 40;
    const growthRate = 0.02; // 2% growth per period - slow
    
    for (let i = 0; i < pointCount; i++) {
        const baseValue = startValue * Math.pow(1 + growthRate, i);
        values.push(addNoise(baseValue, 0.05));
    }
    
    return {
        id: 'slow-rise',
        name: 'Slow Rise (Edge Case)',
        description: 'Gradual rise - steady improvement toward Power',
        expectedCondition: Condition.AFFLUENCE, // Rising toward Power = Affluence
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate edge case: Volatile pattern
 */
export function generateVolatile(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const baseValue = 50;
    const values = Array(pointCount).fill(0).map((_, i) => {
        // High frequency, high amplitude oscillation
        const wave = Math.sin(i * 0.8) * 15;
        return addNoise(baseValue + wave, 0.1);
    });
    
    return {
        id: 'volatile',
        name: 'Volatile (Edge Case)',
        description: 'High fluctuation - unstable but no clear trend',
        expectedCondition: Condition.NORMAL, // Volatile around a mean is still "normal"
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate edge case: V-shaped recovery
 */
export function generateVRecovery(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const startValue = 80;
    const minValue = 20;
    const midPoint = Math.floor(pointCount / 2);
    
    for (let i = 0; i < pointCount; i++) {
        let value: number;
        
        if (i < midPoint) {
            // Sharp drop
            const progress = i / midPoint;
            value = startValue - (startValue - minValue) * progress;
        } else {
            // Sharp recovery - back to original
            const progress = (i - midPoint) / (pointCount - midPoint);
            value = minValue + (startValue - minValue) * progress;
        }
        
        values.push(addNoise(value, 0.05));
    }
    
    return {
        id: 'v-recovery',
        name: 'V-Shaped Recovery (Edge Case)',
        description: 'Sharp drop then full recovery - strong Emergency',
        expectedCondition: Condition.EMERGENCY,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate edge case: High start with gradual decline
 * This should NOT be Power - it's Emergency (declining from high)
 */
export function generateHighDecline(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const highValue = 95;
    const values = Array(pointCount).fill(0).map((_, i) => {
        // Starts high but has downward trend with fluctuation
        const wave = Math.sin(i * 0.2) * 3;
        const trend = -i * 0.5; // Downward trend
        return addNoise(highValue + wave + trend, 0.03);
    });
    
    return {
        id: 'high-decline',
        name: 'High Decline (Edge Case)',
        description: 'Starts high but declining - should be Emergency not Power',
        expectedCondition: Condition.EMERGENCY, // NOT Power - it's declining!
        dataPoints: createDataPoints(values, dates)
    };
}

// ============================================================================
// VARIETY SCENARIOS - Different positions and patterns on the graph
// ============================================================================

/**
 * Low stable - Normal operation at low values (around 25)
 */
export function generateLowStable(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const baseValue = 25;
    const values = Array(pointCount).fill(0).map(() => addNoise(baseValue, 0.08));
    
    return {
        id: 'low-stable',
        name: 'Low Stable',
        description: 'Normal operation at low levels (around 25)',
        expectedCondition: Condition.NORMAL,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Mid-High stable - Normal operation at higher values (around 75)
 */
export function generateMidHighStable(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const baseValue = 75;
    const values = Array(pointCount).fill(0).map(() => addNoise(baseValue, 0.08));
    
    return {
        id: 'mid-high-stable',
        name: 'Mid-High Stable',
        description: 'Normal operation at higher levels (around 75)',
        expectedCondition: Condition.NORMAL,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * High crash to low - Severe danger, starts high crashes to near zero
 */
export function generateHighCrash(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const startValue = 90;
    const decayRate = 0.15; // Severe 15% decay per period
    
    for (let i = 0; i < pointCount; i++) {
        const baseValue = startValue * Math.pow(1 - decayRate, i);
        values.push(Math.max(3, addNoise(baseValue, 0.08))); // Don't go below 3
    }
    
    return {
        id: 'high-crash',
        name: 'High Crash',
        description: 'Starts at power level, crashes to near zero',
        expectedCondition: Condition.DANGER,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Low spike - Starts low, spikes up dramatically
 */
export function generateLowSpike(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const startValue = 15;
    const growthRate = 0.10; // 10% growth per period
    
    for (let i = 0; i < pointCount; i++) {
        const baseValue = startValue * Math.pow(1 + growthRate, i);
        values.push(addNoise(baseValue, 0.06));
    }
    
    return {
        id: 'low-spike',
        name: 'Low Spike',
        description: 'Starts near bottom, spikes up dramatically',
        expectedCondition: Condition.AFFLUENCE,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Normal to Power - Gradual rise from normal to power territory
 */
export function generateNormalToPower(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const startValue = 50;
    const endValue = 95;
    const step = (endValue - startValue) / pointCount;
    
    for (let i = 0; i < pointCount; i++) {
        const baseValue = startValue + (step * i);
        values.push(addNoise(baseValue, 0.05));
    }
    
    return {
        id: 'normal-to-power',
        name: 'Normal to Power',
        description: 'Rises from normal operation into power territory',
        expectedCondition: Condition.AFFLUENCE,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Power to Normal - Drops from power to normal levels
 */
export function generatePowerToNormal(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const startValue = 95;
    const endValue = 50;
    const step = (endValue - startValue) / pointCount;
    
    for (let i = 0; i < pointCount; i++) {
        const baseValue = startValue + (step * i);
        values.push(addNoise(baseValue, 0.05));
    }
    
    return {
        id: 'power-to-normal',
        name: 'Power to Normal',
        description: 'Falls from power level down to normal',
        expectedCondition: Condition.EMERGENCY,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Double dip - Drops, recovers partially, drops again
 */
export function generateDoubleDip(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const thirdPoint = Math.floor(pointCount / 3);
    const twoThirdPoint = Math.floor(pointCount * 2 / 3);
    
    for (let i = 0; i < pointCount; i++) {
        let baseValue: number;
        if (i < thirdPoint) {
            // First drop: 70 -> 30
            baseValue = 70 - (40 * i / thirdPoint);
        } else if (i < twoThirdPoint) {
            // Recovery: 30 -> 55
            const progress = (i - thirdPoint) / (twoThirdPoint - thirdPoint);
            baseValue = 30 + (25 * progress);
        } else {
            // Second drop: 55 -> 25
            const progress = (i - twoThirdPoint) / (pointCount - twoThirdPoint);
            baseValue = 55 - (30 * progress);
        }
        values.push(addNoise(baseValue, 0.06));
    }
    
    return {
        id: 'double-dip',
        name: 'Double Dip',
        description: 'Drops, recovers partially, then drops again',
        expectedCondition: Condition.DANGER,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Rise then plateau - Sharp rise followed by stable high
 */
export function generateRiseThenPlateau(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const riseEnd = Math.floor(pointCount * 0.4);
    
    for (let i = 0; i < pointCount; i++) {
        let baseValue: number;
        if (i < riseEnd) {
            // Rising phase: 30 -> 92
            const progress = i / riseEnd;
            baseValue = 30 + (62 * progress);
        } else {
            // Plateau phase: stable at 92
            baseValue = 92;
        }
        values.push(addNoise(baseValue, 0.03));
    }
    
    return {
        id: 'rise-then-plateau',
        name: 'Rise Then Plateau',
        description: 'Sharp rise followed by stable high (reaches Power)',
        expectedCondition: Condition.POWER, // Ends in Power territory, stable
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Plateau then drop - Stable high followed by decline
 */
export function generatePlateauThenDrop(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const dropStart = Math.floor(pointCount * 0.5);
    
    for (let i = 0; i < pointCount; i++) {
        let baseValue: number;
        if (i < dropStart) {
            // Plateau phase: stable at 90
            baseValue = 90;
        } else {
            // Dropping phase: 90 -> 45
            const progress = (i - dropStart) / (pointCount - dropStart);
            baseValue = 90 - (45 * progress);
        }
        values.push(addNoise(baseValue, 0.04));
    }
    
    return {
        id: 'plateau-then-drop',
        name: 'Plateau Then Drop',
        description: 'Stable high followed by significant decline',
        expectedCondition: Condition.EMERGENCY,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Low volatile - Fluctuating around low values
 */
export function generateLowVolatile(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const baseValue = 25;
    const values = Array(pointCount).fill(0).map((_, i) => {
        const wave = Math.sin(i * 0.7) * 10; // ±10 swing around 25
        return addNoise(baseValue + wave, 0.1);
    });
    
    return {
        id: 'low-volatile',
        name: 'Low Volatile',
        description: 'Fluctuating around low values (15-35 range)',
        expectedCondition: Condition.NORMAL,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * High volatile - Fluctuating around high values
 */
export function generateHighVolatile(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const baseValue = 85;
    const values = Array(pointCount).fill(0).map((_, i) => {
        const wave = Math.sin(i * 0.7) * 8; // ±8 swing around 85
        return addNoise(baseValue + wave, 0.05);
    });
    
    return {
        id: 'high-volatile',
        name: 'High Volatile',
        description: 'Fluctuating around high values (75-95 range)',
        expectedCondition: Condition.POWER, // High enough average with acceptable variance
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Climbing from non-existence - Gradual climb from near zero
 */
export function generateClimbFromZero(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const startValue = 5;
    const endValue = 55;
    const step = (endValue - startValue) / pointCount;
    
    for (let i = 0; i < pointCount; i++) {
        const baseValue = startValue + (step * i);
        values.push(addNoise(baseValue, 0.08));
    }
    
    return {
        id: 'climb-from-zero',
        name: 'Climb From Zero',
        description: 'Gradual climb from near non-existence to normal',
        expectedCondition: Condition.AFFLUENCE,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Falling to non-existence - Decline toward zero
 */
export function generateFallToZero(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const startValue = 45;
    const endValue = 8;
    const step = (endValue - startValue) / pointCount;
    
    for (let i = 0; i < pointCount; i++) {
        const baseValue = startValue + (step * i);
        values.push(addNoise(baseValue, 0.08));
    }
    
    return {
        id: 'fall-to-zero',
        name: 'Fall To Zero',
        description: 'Declining from normal toward non-existence',
        expectedCondition: Condition.DANGER,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Staircase up - Step increases with plateaus
 */
export function generateStaircaseUp(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const steps = 4;
    const stepSize = pointCount / steps;
    const levels = [25, 45, 65, 85];
    
    for (let i = 0; i < pointCount; i++) {
        const stepIndex = Math.min(Math.floor(i / stepSize), levels.length - 1);
        values.push(addNoise(levels[stepIndex], 0.05));
    }
    
    return {
        id: 'staircase-up',
        name: 'Staircase Up',
        description: 'Step increases with plateaus between',
        expectedCondition: Condition.AFFLUENCE,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Staircase down - Step decreases with plateaus
 */
export function generateStaircaseDown(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const steps = 4;
    const stepSize = pointCount / steps;
    const levels = [85, 65, 45, 25];
    
    for (let i = 0; i < pointCount; i++) {
        const stepIndex = Math.min(Math.floor(i / stepSize), levels.length - 1);
        values.push(addNoise(levels[stepIndex], 0.05));
    }
    
    return {
        id: 'staircase-down',
        name: 'Staircase Down',
        description: 'Step decreases with plateaus between',
        expectedCondition: Condition.DANGER,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * W-pattern - Drop, recover, drop, recover (double emergency)
 */
export function generateWPattern(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const quarterPoint = Math.floor(pointCount / 4);
    
    for (let i = 0; i < pointCount; i++) {
        let baseValue: number;
        if (i < quarterPoint) {
            // First drop: 70 -> 30
            baseValue = 70 - (40 * i / quarterPoint);
        } else if (i < quarterPoint * 2) {
            // First recovery: 30 -> 65
            const progress = (i - quarterPoint) / quarterPoint;
            baseValue = 30 + (35 * progress);
        } else if (i < quarterPoint * 3) {
            // Second drop: 65 -> 25
            const progress = (i - quarterPoint * 2) / quarterPoint;
            baseValue = 65 - (40 * progress);
        } else {
            // Second recovery: 25 -> 60
            const progress = (i - quarterPoint * 3) / quarterPoint;
            baseValue = 25 + (35 * progress);
        }
        values.push(addNoise(baseValue, 0.06));
    }
    
    return {
        id: 'w-pattern',
        name: 'W Pattern',
        description: 'Double drop and recovery (W shape)',
        expectedCondition: Condition.EMERGENCY,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * M-pattern - Rise, drop, rise, drop (inverse W)
 */
export function generateMPattern(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    const quarterPoint = Math.floor(pointCount / 4);
    
    for (let i = 0; i < pointCount; i++) {
        let baseValue: number;
        if (i < quarterPoint) {
            // First rise: 30 -> 75
            baseValue = 30 + (45 * i / quarterPoint);
        } else if (i < quarterPoint * 2) {
            // First drop: 75 -> 40
            const progress = (i - quarterPoint) / quarterPoint;
            baseValue = 75 - (35 * progress);
        } else if (i < quarterPoint * 3) {
            // Second rise: 40 -> 70
            const progress = (i - quarterPoint * 2) / quarterPoint;
            baseValue = 40 + (30 * progress);
        } else {
            // Second drop: 70 -> 35
            const progress = (i - quarterPoint * 3) / quarterPoint;
            baseValue = 70 - (35 * progress);
        }
        values.push(addNoise(baseValue, 0.06));
    }
    
    return {
        id: 'm-pattern',
        name: 'M Pattern',
        description: 'Double rise and fall (M shape)',
        expectedCondition: Condition.DANGER, // Ends lower than starts, overall decline
        dataPoints: createDataPoints(values, dates)
    };
}

// ============================================================================
// ANGLE-CONTROLLED TEST SCENARIOS
// These specifically target visual angle thresholds for classification
// ============================================================================

/**
 * Generate steep angle decline (Danger, not Non-Existence)
 * 
 * A GRADUAL decline from high to low over the entire timeline
 * is DANGER, not Non-Existence. Non-Existence requires a FAST crash.
 * 
 * Target: Visual angle < -60° but over full timeline = DANGER
 */
export function generateAngleNonExistence(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    // Start high (100), decline to near zero over ENTIRE timeline
    // This is a steep GRADUAL decline = DANGER (not Non-Existence)
    const startValue = 100;
    const endValue = 5;
    const slopePerPoint = (endValue - startValue) / (pointCount - 1); // ≈ -3.28
    
    for (let i = 0; i < pointCount; i++) {
        const value = startValue + (slopePerPoint * i);
        values.push(Math.max(1, addNoise(value, 0.02)));
    }
    
    return {
        id: 'angle-non-existence',
        name: 'Angle: Steep Gradient (Danger)',
        description: 'Steep decline over full timeline = DANGER (not crash pattern)',
        expectedCondition: Condition.DANGER, // Changed from NON_EXISTENCE
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate DANGER with specific angle: Steep decline
 * Target: Visual angle between -30° and -60°
 * 
 * For angle = -45° in a normalized 1:1 space:
 * tan(-45°) = -1
 * We need: slope * (n-1) / yRange = -1
 * 
 * With 30 points and yRange from 80 to 20 (range = 60):
 * slope = -1 * 60 / 29 ≈ -2.07 per period
 * visually this gives a 45° angle
 */
export function generateAngleDanger(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    // Start at 80, decline to 20 (60 point drop over 30 periods)
    const startValue = 80;
    const endValue = 20;
    const slopePerPoint = (endValue - startValue) / (pointCount - 1); // ≈ -2.07
    
    for (let i = 0; i < pointCount; i++) {
        const value = startValue + (slopePerPoint * i);
        values.push(addNoise(value, 0.03));
    }
    
    return {
        id: 'angle-danger',
        name: 'Angle: Danger (-30° to -60°)',
        description: 'Steep decline, ~45° angle. Target angle: between -30° and -60°',
        expectedCondition: Condition.DANGER,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate EMERGENCY with specific angle: Gradual decline
 * Target: Visual angle between -5° and -30°
 * 
 * For angle = -15° in a normalized 1:1 space:
 * tan(-15°) ≈ -0.27
 * We need: slope * (n-1) / yRange = -0.27
 * 
 * With 30 points and yRange from 60 to 45 (range = 15):
 * slope = -0.27 * 15 / 29 ≈ -0.14 per period
 */
export function generateAngleEmergency(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    // Start at 60, gentle decline to 45 (15 point drop over 30 periods)
    const startValue = 60;
    const endValue = 45;
    const slopePerPoint = (endValue - startValue) / (pointCount - 1); // ≈ -0.52
    
    for (let i = 0; i < pointCount; i++) {
        const value = startValue + (slopePerPoint * i);
        values.push(addNoise(value, 0.04));
    }
    
    return {
        id: 'angle-emergency',
        name: 'Angle: Emergency (-5° to -30°)',
        description: 'Gradual decline, ~15° angle. Target angle: between -5° and -30°',
        expectedCondition: Condition.EMERGENCY,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate NORMAL with flat angle: Nearly horizontal
 * Target: Visual angle near 0° (flat)
 */
export function generateAngleNormal(pointCount = 30): TestScenario {
    const dates = generateDates(pointCount);
    const values: number[] = [];
    
    // Flat line around 50 with minimal slope
    const baseValue = 50;
    const verySmallSlope = 0.03; // Tiny upward drift, visually flat
    
    for (let i = 0; i < pointCount; i++) {
        const value = baseValue + (verySmallSlope * i);
        values.push(addNoise(value, 0.05));
    }
    
    return {
        id: 'angle-normal',
        name: 'Angle: Normal (near 0°)',
        description: 'Nearly flat line, minimal angle. Target angle: near 0°',
        expectedCondition: Condition.NORMAL,
        dataPoints: createDataPoints(values, dates)
    };
}

/**
 * Generate all test scenarios
 */
export function generateAllTestScenarios(pointCount = 30): TestScenario[] {
    return [
        // Core conditions
        generateNonExistence(pointCount),
        generateDanger(pointCount),
        generateEmergency(pointCount),
        generateNormal(pointCount),
        generateAffluence(pointCount),
        generatePower(pointCount),
        // Edge cases
        generateSlowDecline(pointCount),
        generateSlowRise(pointCount),
        generateVolatile(pointCount),
        generateVRecovery(pointCount),
        generateHighDecline(pointCount),
        // Variety - Different positions
        generateLowStable(pointCount),
        generateMidHighStable(pointCount),
        generateLowVolatile(pointCount),
        generateHighVolatile(pointCount),
        // Variety - Transitions
        generateHighCrash(pointCount),
        generateLowSpike(pointCount),
        generateNormalToPower(pointCount),
        generatePowerToNormal(pointCount),
        generateClimbFromZero(pointCount),
        generateFallToZero(pointCount),
        // Variety - Complex patterns
        generateDoubleDip(pointCount),
        generateRiseThenPlateau(pointCount),
        generatePlateauThenDrop(pointCount),
        generateStaircaseUp(pointCount),
        generateStaircaseDown(pointCount),
        generateWPattern(pointCount),
        generateMPattern(pointCount),
        // Angle-controlled tests for down-trend classification
        generateAngleNonExistence(pointCount),
        generateAngleDanger(pointCount),
        generateAngleEmergency(pointCount),
        generateAngleNormal(pointCount)
    ];
}

/**
 * Convert TestScenario to Firestore-compatible format
 */
export function toFirestoreFormat(scenario: TestScenario): TestScenarioFirestore {
    return {
        id: scenario.id,
        name: scenario.name,
        description: scenario.description,
        expectedCondition: scenario.expectedCondition,
        dataPoints: scenario.dataPoints.map(dp => ({
            date: dp.date.toISOString(),
            value: dp.value,
            valueCompounded: dp.valueCompounded,
            index: dp.index
        })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    };
}

/**
 * Convert Firestore format back to TestScenario
 */
export function fromFirestoreFormat(doc: TestScenarioFirestore): TestScenario {
    return {
        id: doc.id,
        name: doc.name,
        description: doc.description,
        expectedCondition: doc.expectedCondition,
        dataPoints: doc.dataPoints.map((dp: AnalyzerDataPointFirestore) => ({
            date: new Date(dp.date),
            value: dp.value,
            valueCompounded: dp.valueCompounded,
            index: dp.index
        }))
    };
}

/**
 * Generate monthly test data (fewer points, larger intervals)
 */
export function generateMonthlyScenarios(): TestScenario[] {
    return generateAllTestScenarios(12); // 12 months
}

/**
 * Generate daily test data
 */
export function generateDailyScenarios(): TestScenario[] {
    return generateAllTestScenarios(30); // 30 days
}
